// Analytics utility functions for tracking custom events

// Track meeting creation
export const trackMeetingCreated = (meetingData) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'meeting_created', {
      meeting_id: meetingData?.id,
      participant_count: meetingData?.participantCount || 0,
      meeting_date: meetingData?.meetingDate,
      location_type: meetingData?.locationType,
      page: window.location?.pathname
    });
  }
};

// Track meeting confirmation
export const trackMeetingConfirmed = (meetingId, participantName) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'meeting_confirmed', {
      meeting_id: meetingId,
      participant_name: participantName,
      page: window.location?.pathname
    });
  }
};

// Track location selection
export const trackLocationSelected = (locationData) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'location_selected', {
      location_name: locationData?.name,
      location_type: locationData?.type,
      location_rating: locationData?.rating,
      has_phone: !!locationData?.phone,
      page: window.location?.pathname
    });
  }
};

// Track favorite location (when user selects same location type multiple times)
export const trackFavoriteLocation = (locationType, count) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'favorite_location_pattern', {
      location_type: locationType,
      selection_count: count,
      page: window.location?.pathname
    });
  }
};

// Track travel time trends
export const trackTravelTime = (travelData) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'travel_time_recorded', {
      travel_time_minutes: travelData?.travelTime,
      distance_km: travelData?.distance,
      transport_mode: travelData?.transportMode || 'driving',
      page: window.location?.pathname
    });
  }
};

// Track meeting status changes
export const trackMeetingStatusChange = (meetingId, oldStatus, newStatus) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'meeting_status_changed', {
      meeting_id: meetingId,
      old_status: oldStatus,
      new_status: newStatus,
      page: window.location?.pathname
    });
  }
};

// Track active meeting engagement
export const trackActiveMeetingView = (meetingId, participantCount) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'active_meeting_viewed', {
      meeting_id: meetingId,
      participant_count: participantCount,
      page: window.location?.pathname
    });
  }
};

// Track emergency contact access
export const trackEmergencyContactAccess = (meetingId) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'emergency_contact_accessed', {
      meeting_id: meetingId,
      page: window.location?.pathname
    });
  }
};

// Track notification settings changes
export const trackNotificationSettingsChanged = (settingType, enabled) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'notification_settings_changed', {
      setting_type: settingType,
      enabled: enabled,
      page: window.location?.pathname
    });
  }
};

// Track filter usage
export const trackFilterUsed = (filterType, filterValue) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'filter_used', {
      filter_type: filterType,
      filter_value: filterValue,
      page: window.location?.pathname
    });
  }
};

// Track button clicks for engagement
export const trackButtonClick = (buttonId, buttonLabel) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'button_click', {
      button_id: buttonId,
      button_label: buttonLabel,
      page: window.location?.pathname
    });
  }
};

// Track user engagement time on page
export const trackEngagementTime = (pageName, timeSpentSeconds) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'user_engagement', {
      page_name: pageName,
      engagement_time_seconds: timeSpentSeconds,
      page: window.location?.pathname
    });
  }
};

// Track search/input actions
export const trackSearchAction = (searchType, searchQuery) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'search', {
      search_term: searchQuery,
      search_type: searchType,
      page: window.location?.pathname
    });
  }
};

/**
 * Track JavaScript errors to Google Analytics
 */
export const trackError = (error, errorInfo = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'exception', {
      description: error?.message || 'Unknown error',
      fatal: errorInfo?.fatal || false,
      error_type: error?.name || 'Error',
      error_stack: error?.stack?.substring(0, 150) || 'No stack trace',
      component_stack: errorInfo?.componentStack?.substring(0, 150) || '',
      page: window.location?.pathname
    });
  }

  // Also log to console in development
  if (import.meta.env?.DEV) {
    console.error('Error tracked:', error, errorInfo);
  }
};

/**
 * Track validation errors
 */
export const trackValidationError = (fieldName, errorMessage, formName) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'validation_error', {
      field_name: fieldName,
      error_message: errorMessage,
      form_name: formName,
      page: window.location?.pathname
    });
  }
};

/**
 * Track API/submission errors
 */
export const trackSubmissionError = (errorType, errorMessage, context = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'submission_error', {
      error_type: errorType,
      error_message: errorMessage,
      context: JSON.stringify(context)?.substring(0, 100),
      page: window.location?.pathname
    });
  }
};

/**
 * Track component render errors
 */
export const trackComponentError = (componentName, error, errorInfo) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'component_error', {
      component_name: componentName,
      error_message: error?.message || 'Unknown error',
      error_stack: error?.stack?.substring(0, 150) || '',
      component_stack: errorInfo?.componentStack?.substring(0, 150) || '',
      page: window.location?.pathname
    });
  }
};